import { store } from '../core/store.js';

let splideInstances = [];

// Duración y curva de la transición entre diapositivas. Salen a constante porque no las usa
// solo Splide: se publican en `--n3i-splide-speed` sobre el contenedor para que el CSS pueda
// SINCRONIZARSE con ellas (ver la paginación del carrusel en site.css). Splide marca el punto
// activo al EMPEZAR el movimiento, así que cualquier indicador sin transición salta al
// instante mientras la imagen aún está cruzando: durante ese rato los puntos dicen una cosa y
// la pantalla enseña otra. Atarlo a la MISMA constante evita que se separen al retocar una.
const SPLIDE_SPEED = 800;
const SPLIDE_EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';

// ── Lectura de los data-splide-* del contenedor ─────────────────────────────
// CONVENCIÓN DEL PROYECTO: atributos SUELTOS (`data-splide-per-page`, `-type`, …), NO el
// JSON `data-splide` nativo de Splide. Los cuatro primeros ya existían; los añadidos por el
// bloque `carrusel` (-interval, -arrows, -pagination, -pause-on-hover, -navigation, -sync)
// siguen la misma convención Y **caen a los defaults de siempre cuando el atributo no
// viene**: `gallery-v2` no declara ninguno de ellos y tiene que seguir montándose EXACTAMENTE
// igual que antes (flechas + puntos + 4000 ms + pausa al pasar el ratón).
const attrBool = (el, nombre, porDefecto) => {
    const v = el.getAttribute(nombre);
    if (v === null || v === '') return porDefecto;
    return v === 'true' || v === '1';
};
const attrInt = (el, nombre, porDefecto) => {
    const v = parseInt(el.getAttribute(nombre), 10);
    return Number.isNaN(v) ? porDefecto : v;
};

// Movimiento reducido: NO es configurable a propósito. Un carrusel que se mueve solo es el
// caso de libro de "animación que el usuario no puede detener"; si el sistema operativo pide
// menos movimiento, no hay autoplay (las flechas y los puntos siguen funcionando).
const prefiereMenosMovimiento = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function initSplideCarousels() {
    if (typeof window.Splide === 'undefined') return;

    // Observer ultra-preciso para contenedores individuales
    const splideObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
            const target = entry.target;
            const instance = splideInstances.find(s => s.root === target);
            if (instance) {
                // Usar requestAnimationFrame previene loops de ResizeObserver si el refresh cambia dimensiones
                requestAnimationFrame(() => {
                    try { instance.refresh(); } catch(e) {}
                });
            }
        }
    });

    // Empareja el carrusel PRINCIPAL con su tira de miniaturas (carrusel v4). El principal
    // declara `data-splide-sync="<id de la tira>"`; la tira lleva ese id y
    // `data-splide-navigation="true"`. Splide 4.1.4 admite `sync()` DESPUÉS de mount (si el
    // estado es IDLE re-monta el componente Sync), así que no hace falta reordenar el montaje
    // que ya usa gallery-v2. Idempotente vía `data-n3i-synced`.
    function syncCarousels() {
        document.querySelectorAll('.splide[data-splide-sync]').forEach(el => {
            if (el.dataset.n3iSynced === '1') return;
            const otro = document.getElementById(el.getAttribute('data-splide-sync'));
            if (!otro) return;
            const principal = splideInstances.find(s => s.root === el);
            const tira = splideInstances.find(s => s.root === otro);
            if (!principal || !tira) return;
            try {
                principal.sync(tira);
                el.dataset.n3iSynced = '1';
            } catch (e) { /* sin sincronía, ambos siguen funcionando por separado */ }
        });
    }

    function mountCarousels() {
        const splides = document.querySelectorAll('.splide');
        splides.forEach(el => {
            // Si ya está inicializado, saltar
            if (el.classList.contains('is-initialized')) return;

            // Configuración dinámica vía data attributes
            const perPage = parseInt(el.getAttribute('data-splide-per-page')) || 3;
            const type = el.getAttribute('data-splide-type') || 'slide';
            const gap = el.getAttribute('data-splide-gap') || '2rem';
            const autoplay = el.getAttribute('data-splide-autoplay') === 'true';

            const splide = new window.Splide(el, {
                type: type,
                perPage: perPage,
                perMove: 1,
                gap: perPage === 1 ? 0 : gap,
                pagination: attrBool(el, 'data-splide-pagination', true),
                arrows: attrBool(el, 'data-splide-arrows', true),
                drag: true,
                speed: SPLIDE_SPEED,
                easing: SPLIDE_EASING,
                // El intervalo es del BLOQUE (el cliente lo gradúa en el carrusel); 4000 ms es
                // el valor histórico y sigue siendo el default cuando nadie declara nada.
                autoplay: autoplay && !prefiereMenosMovimiento(),
                interval: attrInt(el, 'data-splide-interval', 4000),
                pauseOnHover: attrBool(el, 'data-splide-pause-on-hover', true),
                pauseOnFocus: true,
                // `type:'fade'` (carrusel v1) NO da la vuelta: se planta en la última
                // diapositiva y el autoplay se queda ahí parado. `rewind` es lo que la
                // devuelve a la primera y mantiene la rotación viva. En `type:'loop'`
                // Splide lo ignora (el bucle ya envuelve), así que emitirlo es inocuo.
                // Default FALSE: `gallery-v2` no declara el atributo y conserva su
                // comportamiento histórico exacto.
                rewind: attrBool(el, 'data-splide-rewind', false),
                // Tira de miniaturas (carrusel v4): en modo navegación, pulsar una miniatura
                // lleva la diapositiva principal a esa posición.
                isNavigation: attrBool(el, 'data-splide-navigation', false),
                breakpoints: {
                    1024: { perPage: Math.min(2, perPage) },
                    640: { perPage: 1 }
                }
            });
            // Duración y curva REALES al CSS, para que lo que se sincroniza con la
            // transición (los puntos del carrusel) no las repita a mano.
            el.style.setProperty('--n3i-splide-speed', SPLIDE_SPEED + 'ms');
            el.style.setProperty('--n3i-splide-easing', SPLIDE_EASING);
            splide.mount();
            splideInstances.push(splide);
            splideObserver.observe(el);

            // Para gallery-v2: mostrar solo después de refresh con dimensiones correctas
            // scale(0.98) de .reveal hace que is-initialized tenga medidas erróneas
            // Esperamos 900ms (> 700ms de la transición CSS de .reveal) para refrescar
            if (el.classList.contains('gallery-v2-splide')) {
                setTimeout(() => {
                    try { splide.refresh(); } catch(e) {}
                    el.classList.add('splide-ready');
                }, 900);
            }
        });
        syncCarousels();
    }

    function destroyCarousels() {
        splideInstances.forEach(splide => {
            splideObserver.unobserve(splide.root);
            // El emparejamiento muere con la instancia: si no se limpia la marca, al volver a
            // montar (toggle Editor → Ver Sitio) la tira de miniaturas dejaría de sincronizarse.
            if (splide.root && splide.root.dataset) delete splide.root.dataset.n3iSynced;
            splide.destroy(false); // Eliminamos la funcionalidad pero dejamos el HTML para que el CSS fallback de editor funcione
        });
        splideInstances = [];
    }

    // Evaluación inicial
    if (!store.get('editorMode')) {
        mountCarousels();
    }

    // Hook para recargar Splide cuando el DOM cambie (e.g. al generar página)
    window.refreshSplide = () => {
        if (!store.get('editorMode')) {
            splideInstances = splideInstances.filter(s => {
                if (s.root && document.contains(s.root)) return true;
                try { s.destroy(); } catch(e) {}
                return false;
            });
            mountCarousels();
        }
    };

    // Hook para togglear entre Modo Editor y Modo Vista
    window.toggleSplide = (isEditorMode) => {
        if (isEditorMode) {
            destroyCarousels();
        } else {
            mountCarousels();
        }
    };
}
